import React, { Component, ErrorInfo } from 'react';
import { logError } from '@/lib/supabase';
import { RefreshCw, Mail, AlertTriangle, RotateCcw, Copy, Bug } from 'lucide-react';
import { logger } from '@/lib/logger';
import { isChunkError, getReloadAttempts } from '@/lib/chunk-error-handler';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  copied: boolean;
  showStack: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: null,
    copied: false,
    showStack: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId,
      copied: false,
      showStack: false,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(`Error Boundary caught error in ${this.props.name || 'app'}`, error, {
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
    });
    logError(error, `Error Boundary [${this.props.name || 'app'}]: ${errorInfo.componentStack}`);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
      showStack: false,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private copyErrorInfo = () => {
    const errorText = [
      `Error ID: ${this.state.errorId}`,
      `Component: ${this.props.name || 'app'}`,
      `Error: ${this.state.error?.message}`,
      `Stack: ${this.state.error?.stack}`,
      `Component Stack: ${this.state.errorInfo?.componentStack}`,
      `Timestamp: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User Agent: ${navigator.userAgent}`,
    ].join('\n');

    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {
      logger.error('Failed to copy error info to clipboard');
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if this is a chunk load error (app update) or a real error
      const isChunk = this.state.error ? isChunkError(this.state.error) : false;
      const reloadAttempts = getReloadAttempts();

      // Render friendly "Update Available" UI for chunk errors
      // Uses inline styles as primary because CSS chunks may also be unavailable
      if (isChunk) {
        return (
          <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-background p-4 pt-8 pb-safe overflow-auto"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              backgroundColor: '#fff',
              padding: '2rem 1rem',
              overflow: 'auto',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            <div
              className="max-w-md w-full space-y-6 text-center my-auto"
              style={{
                maxWidth: '28rem',
                width: '100%',
                textAlign: 'center',
                margin: 'auto 0',
              }}
            >
              {/* Meme/GIF */}
              <div
                className="w-full max-w-sm mx-auto mb-4 bg-muted rounded-lg flex items-center justify-center overflow-hidden"
                style={{
                  maxWidth: '24rem',
                  margin: '0 auto 1rem',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/images/pages/Update.gif"
                  alt="App updated"
                  className="w-full h-auto object-contain"
                  style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h1
                  className="text-2xl font-bold"
                  style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}
                >
                  JD Simplified Was Just Updated
                </h1>
                <p
                  className="text-center text-muted-foreground"
                  style={{ color: '#6b7280', textAlign: 'center' }}
                >
                  We&apos;ve updated the app with new features and improvements.
                  Please refresh the page to continue.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <button
                  onClick={this.handleReload}
                  className="w-full"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#18181b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <RefreshCw style={{ width: '1rem', height: '1rem' }} className="w-4 h-4" />
                  Refresh Page
                </button>

                <button
                  onClick={() => window.location.href = 'mailto:support@jdsimplified.com?subject=Issue%20After%20Update'}
                  className="w-full"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'transparent',
                    color: '#18181b',
                    border: '1px solid #e4e4e7',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <Mail style={{ width: '1rem', height: '1rem' }} className="w-4 h-4" />
                  Contact Support
                </button>
              </div>

              <p
                className="text-xs text-muted-foreground"
                style={{ fontSize: '0.75rem', color: '#9ca3af' }}
              >
                If refreshing doesn&apos;t work, please contact us and we&apos;ll help right away.
              </p>

              {process.env.NODE_ENV === 'development' && (
                <div
                  className="mt-4 p-4 bg-muted rounded-lg text-left"
                  style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: '#f4f4f5',
                    borderRadius: '0.5rem',
                    textAlign: 'left',
                  }}
                >
                  <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Debug Info: Chunk load error detected
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Reload attempts: {reloadAttempts}
                  </p>
                  {this.state.error && (
                    <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>
                      {this.state.error.toString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      // Render responsive error UI for real application errors
      // CSS is available here (only chunk errors lose CSS)
      const componentLabel = this.props.name || 'this section';

      return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-background p-4 pt-8 pb-safe overflow-auto">
          <div className="max-w-md w-full space-y-6 text-center my-auto">
            {/* GIF */}
            <div className="w-full max-w-sm mx-auto mb-4 bg-muted rounded-lg overflow-hidden">
              <img
                src="/images/pages/Something_Went_Wrong.gif"
                alt="Something went wrong"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <h1 className="text-2xl font-bold text-foreground">
                  Something Went Wrong
                </h1>
              </div>
              <p className="text-center text-muted-foreground">
                An unexpected error occurred in {componentLabel}. Please try again or refresh the page.
              </p>
            </div>

            {/* Dev-only error details */}
            {import.meta.env.DEV && this.state.error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-left">
                <p className="font-semibold text-destructive text-sm mb-1">Error Details:</p>
                <p className="text-destructive/80 font-mono text-xs break-all">
                  {this.state.error.message}
                </p>
                {this.state.errorId && (
                  <p className="text-destructive/60 text-xs mt-2">
                    Error ID: {this.state.errorId}
                  </p>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <RotateCcw className="h-4 w-4" />
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-secondary text-secondary-foreground border border-border rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors cursor-pointer"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh Page
              </button>

              <button
                onClick={() => window.location.href = 'mailto:support@jdsimplified.com?subject=Application%20Error'}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-transparent text-muted-foreground border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors cursor-pointer"
              >
                <Mail className="h-4 w-4" />
                Contact Support
              </button>
            </div>

            {/* Dev-only tools: copy error info + stack trace */}
            {import.meta.env.DEV && (
              <div className="pt-4 border-t border-border space-y-2">
                <button
                  onClick={this.copyErrorInfo}
                  className="flex items-center justify-center gap-2 w-full py-2 px-4 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors cursor-pointer"
                >
                  <Copy className="h-4 w-4" />
                  {this.state.copied ? 'Copied!' : 'Copy Error Info'}
                </button>

                {this.state.error?.stack && (
                  <details
                    open={this.state.showStack}
                    onToggle={(e) => this.setState({ showStack: (e.target as HTMLDetailsElement).open })}
                  >
                    <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                      <Bug className="h-4 w-4 inline" />
                      View Stack Trace
                    </summary>
                    <pre className="mt-2 p-3 bg-muted rounded-lg text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap text-left">
                      {this.state.error.stack}
                    </pre>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="mt-1 p-3 bg-muted rounded-lg text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap text-left">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </details>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              If this keeps happening, please contact us and we&apos;ll help right away.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping individual components
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  name?: string
) {
  const WithErrorBoundary = (props: P) => (
    <ErrorBoundary name={name}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithErrorBoundary;
}